import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api/client';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, checkAuth, user } = useAuth();
  const location = useLocation();
  const [processingSession, setProcessingSession] = useState(false);
  const [hasSessionId, setHasSessionId] = useState(false);
  const sessionProcessedRef = useRef(false);

  // Check for session_id immediately on mount and location change
  useEffect(() => {
    const hash = location.hash;
    const urlParams = new URLSearchParams(location.search);
    let sessionId = null;
    
    // Try hash first (format: #session_id=xxx)
    if (hash.includes('session_id=')) {
      sessionId = hash.split('session_id=')[1]?.split('&')[0];
      console.log('🔐 ProtectedRoute - Found session_id in hash:', sessionId);
    }
    
    // Try query parameter (format: ?session_id=xxx)
    if (!sessionId && urlParams.has('session_id')) {
      sessionId = urlParams.get('session_id');
      console.log('🔐 ProtectedRoute - Found session_id in query:', sessionId);
    }

    if (sessionId) {
      console.log('🔐 ProtectedRoute - Session ID detected, will process before authentication check');
      setHasSessionId(true);
    } else {
      console.log('🔐 ProtectedRoute - No session_id in URL');
      setHasSessionId(false);
    }
  }, [location]);

  // Process session_id
  useEffect(() => {
    // Skip if already processed or no session_id
    if (sessionProcessedRef.current || !hasSessionId) {
      return;
    }

    // Skip if already authenticated
    if (isAuthenticated) {
      console.log('🔐 ProtectedRoute - Already authenticated, skipping session processing');
      sessionProcessedRef.current = true;
      return;
    }

    const handleSessionId = async () => {
      const hash = location.hash;
      const urlParams = new URLSearchParams(location.search);
      let sessionId = null;
      
      // Try hash first (format: #session_id=xxx)
      if (hash.includes('session_id=')) {
        sessionId = hash.split('session_id=')[1]?.split('&')[0];
      }
      
      // Try query parameter (format: ?session_id=xxx)
      if (!sessionId && urlParams.has('session_id')) {
        sessionId = urlParams.get('session_id');
      }

      if (!sessionId) {
        console.log('🔐 ProtectedRoute - No session_id found in handleSessionId');
        return;
      }
      
      console.log('🔐 ProtectedRoute - Starting session processing...');
      console.log('🔐 ProtectedRoute - Current state:', { 
        loading, 
        isAuthenticated, 
        hasUser: !!user,
        pathname: location.pathname 
      });
      
      setProcessingSession(true);
      sessionProcessedRef.current = true; // Mark as processed to prevent loops
      
      try {
        console.log('🔐 ProtectedRoute - Calling session API...');
        const response = await api.post('/api/v1/auth/session', null, {
          headers: {
            'X-Session-ID': sessionId
          }
        });

        console.log('🔐 ProtectedRoute - Session API response status:', response.status);

        if (response.status === 200) {
          const data = response.data;
          console.log('🔐 ProtectedRoute - Session validated successfully!', data);
          
          // Clean up URL
          console.log('🔐 ProtectedRoute - Cleaning up URL...');
          window.history.replaceState({}, '', location.pathname);
          
          // Force auth check to update state
          console.log('🔐 ProtectedRoute - Forcing auth state refresh...');
          await checkAuth();
          console.log('🔐 ProtectedRoute - Auth state refreshed, authentication should now be complete');
        } else {
          console.error('🔐 ProtectedRoute - Session validation failed:', {
            status: response.status,
            statusText: response.statusText,
            body: response.data
          });
        }
      } catch (error) {
        console.error('🔐 ProtectedRoute - Session validation error:', {
          message: error.message,
          stack: error.stack
        });
      } finally {
        setProcessingSession(false);
        setHasSessionId(false); // Clear the flag
      }
    };

    // Only run if not loading (initial auth check complete)
    if (!loading) {
      handleSessionId();
    }
  }, [hasSessionId, loading, isAuthenticated, location, checkAuth, user]);

  console.log('🔐 ProtectedRoute - Render decision:', { 
    loading, 
    processingSession, 
    hasSessionId,
    isAuthenticated, 
    hasUser: !!user,
    willShowLoading: loading || processingSession || hasSessionId,
    willRedirect: !loading && !processingSession && !hasSessionId && !isAuthenticated
  });

  // Show loading state while:
  // 1. Initial auth check is in progress (loading)
  // 2. Session is being processed (processingSession)
  // 3. Session ID is detected but not yet processed (hasSessionId)
  if (loading || processingSession || hasSessionId) {
    const message = processingSession 
      ? 'Completing sign in...' 
      : hasSessionId 
        ? 'Validating session...'
        : 'Loading...';
        
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 dark:text-gray-400">{message}</p>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {loading && 'Checking authentication...'}
            {hasSessionId && !processingSession && 'Session ID detected...'}
            {processingSession && 'Authenticating with server...'}
          </p>
        </div>
      </div>
    );
  }

  // Only redirect if we're sure there's no session to process
  if (!isAuthenticated) {
    console.log('🔐 ProtectedRoute - User not authenticated and no session to process, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  console.log('🔐 ProtectedRoute - User authenticated successfully, rendering protected content');
  return children;
};

export default ProtectedRoute;
